import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import { CheckCircle } from "lucide-react";
import img39 from "../../../assets/img39.webp";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function TravelInsuranceHero() {
  const container = "w-full max-w-[1380px] mx-auto px-4 sm:px-6 lg:px-8";

  return (
    <>
      <SEO
        title="Assurance voyage au Canada | Couverture médicale – einsured.ca"
        description="Comparez les assurances voyage au Canada avec couverture médicale et d’urgence pour vos déplacements d’affaires ou de loisirs."
        keywords="Assurance voyage Canada, assurance médicale voyage d'urgence, assurance annulation de voyage, couverture retard de bagages canada"
      />
      {/* SECTION HÉRO */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img39})` }}
      >
        <div className="absolute inset-0 bg-black/40" />

        <div className={`relative z-10 ${container}`}>
          <div className="text-white max-w-2xl">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4">
              Assurance voyage
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Protection pour chaque voyage, incluant les urgences médicales,
              l&apos;interruption de voyage et l&apos;assistance voyage où que vous alliez
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* Assurance voyage pour les Canadiens */}
      <section className="py-16 sm:py-20 md:py-22 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-808">
            <h4 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Assurance voyage pour les Canadiens
            </h4>
            <div className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8 mb-10">
              <p>
                Voyager est une aventure exaltante, que vous exploriez les
                paysages pittoresques du Canada ou que vous partiez vers des
                contrées lointaines. Cependant, des événements imprévus peuvent
                rapidement transformer votre voyage de rêve en cauchemar.
              </p>
              <p>
                Chez{" "}
                <span className="font-semibold text-[#028fdc]">
                  einsured.ca
                </span>
                , nous comprenons l&apos;importance de protéger vos voyages.
                C&apos;est pourquoi nous offrons des régimes d&apos;assurance voyage
                complets conçus pour vous protéger contre les imprévus. Dans ce
                guide, nous vous expliquerons tout ce que vous devez savoir sur
                l&apos;assurance voyage et comment elle peut rendre votre périple
                sans stress.
              </p>
            </div>

            {/* Pourquoi avez-vous besoin d'une assurance voyage */}
            <div className="mt-4 md:py-8">
              <h4 className="text-2xl sm:text-3xl md:text-3xl font-bold text-[#49628b] text-center mb-6">
                Pourquoi avez-vous besoin d&apos;une assurance voyage
              </h4>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 text-gray-800 mb-6 text-left sm:text-center max-w-4xl mx-auto">
                Lorsque vous planifiez un voyage, il est facile de négliger
                l&apos;assurance voyage, mais c&apos;est l&apos;une des étapes les plus
                cruciales que vous puissiez prendre. Voici pourquoi :
              </p>

              <div className="space-y-5 text-base sm:text-lg leading-7 sm:leading-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    <span className="font-semibold text-[#49628b]">
                      Urgences médicales :
                    </span>{" "}
                    Les accidents ou les maladies soudaines peuvent survenir
                    n&apos;importe où. Sans assurance voyage, vous pourriez faire
                    face à des frais médicaux astronomiques, surtout dans les
                    pays où les soins de santé sont coûteux.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    <span className="font-semibold text-[#49628b]">
                      Annulations de voyage :
                    </span>{" "}
                    Des événements imprévus comme des intempéries, des urgences
                    familiales ou des obligations professionnelles soudaines
                    peuvent perturber vos plans de voyage. L&apos;assurance voyage
                    peut vous rembourser les frais non remboursables.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    <span className="font-semibold text-[#49628b]">
                      Bagages perdus ou volés :
                    </span>{" "}
                    Perdre vos bagages ou se faire voler vos effets personnels
                    peut gâcher votre voyage. L&apos;assurance voyage peut vous
                    aider à récupérer le coût de vos biens perdus ou volés.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    <span className="font-semibold text-[#49628b]">
                      Retards de voyage :
                    </span>{" "}
                    Les annulations ou retards de vols peuvent vous laisser
                    bloqué. L&apos;assurance voyage couvre l&apos;hébergement et les
                    repas lors de ces interruptions imprévues.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    <span className="font-semibold text-[#49628b]">
                      Tranquillité d&apos;esprit :
                    </span>{" "}
                    Savoir que vous êtes couvert vous permet de profiter de
                    votre voyage sans vous inquiéter constamment des problèmes
                    potentiels.
                  </p>
                </div>
              </div>
            </div>

            {/* Types d'assurance voyage */}
            <div className="mt-14">
              <h3 className="text-2xl sm:text-3xl md:text-3xl font-bold text-[#49628b] text-center mb-10">
                Types d&apos;assurance voyage
              </h3>

              <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 md:gap-24">
                <div className="flex items-center gap-3 text-center md:text-left">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff]" />
                  <p className="text-lg text-gray-800">
                    Interruption de voyage et retard de bagages
                  </p>
                </div>

                <div className="flex items-center gap-3 text-center md:text-right">
                  <CheckCircle className="h-6 w-6 text-[#00b4ff]" />
                  <p className="text-lg text-gray-800">
                    Assurance médicale d&apos;urgence
                  </p>
                </div>
              </div>

              <div className="mt-12 flex justify-center">
                <Link
                  to="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
                >
                  Commencer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERRUPTION DE VOYAGE ET RETARD DE BAGAGES */}
      <section
        className="relative py-20 sm:py-24 md:py-22 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 ${container}`}>
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Interruption de voyage et retard de bagages
            </h2>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 text-gray-100 px-1 text-left sm:text-center">
              Lorsque vous planifiez un voyage, il est facile de négliger
              l&apos;assurance voyage, mais c&apos;est l&apos;une des étapes les plus
              cruciales que vous puissiez prendre. Voici pourquoi :
            </p>

            <h3 className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8">
              Qu&apos;est-ce que l&apos;assurance interruption de voyage ?
            </h3>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-10 text-gray-100 text-left sm:text-center">
              L&apos;assurance interruption de voyage aide à couvrir les dépenses
              imprévues si votre voyage est écourté ou retardé. Cela peut être
              dû à diverses raisons telles qu&apos;une maladie, des intempéries ou
              des urgences chez vous. L&apos;assurance couvre généralement les
              coûts que vous ne pouvez pas récupérer autrement, tels que :
            </p>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 text-[#49628b]">
                  Frais de voyage prépayés
                </h4>
                <p className="text-sm sm:text-base leading-relaxed">
                  Si vous avez prépayé des séjours à l&apos;hôtel, des excursions
                  ou des vols, l&apos;assurance interruption de voyage peut vous
                  rembourser ces frais si vous devez mettre fin à votre voyage
                  prématurément.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 text-[#49628b]">
                  Frais de voyage de retour
                </h4>
                <p className="text-sm sm:text-base leading-relaxed">
                  Elle peut également couvrir les dépenses supplémentaires pour
                  rentrer chez vous ou reprendre votre voyage si quelque chose
                  perturbe vos plans.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 text-[#49628b]">
                  Hébergement et repas
                </h4>
                <p className="text-sm sm:text-base leading-relaxed">
                  Si vous devez rester plus longtemps en raison de
                  circonstances imprévues, l&apos;assurance peut couvrir les frais
                  d&apos;hébergement et de repas supplémentaires.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assurance retard de bagages */}
      <section className="py-16 sm:py-20 md:py-22 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800 text-center">
            <h2 className="text-3xl sm:text-3xl font-bold text-[#49628b] mb-6">
              Qu&apos;est-ce que l&apos;assurance retard de bagages ?
            </h2>

            <p className="text-base sm:text-lg max-w-4xl mx-auto leading-7 sm:leading-8 mb-12 text-left sm:text-center">
              L&apos;assurance retard de bagages offre une couverture si vos bagages
              enregistrés sont retardés. Cette couverture vous aide à gérer les
              inconvénients et les dépenses imprévues liés à l&apos;absence de vos
              affaires. En général, les assureurs offrent un montant fixe par
              jour pour vous aider à couvrir les articles nécessaires tels que
              les vêtements, les articles de toilette et autres produits
              essentiels.
            </p>

            <h3 className="text-3xl sm:text-3xl font-bold text-[#49628b] mb-10">
              Qu&apos;est-ce qui est couvert par l&apos;assurance interruption de voyage et retard de bagages ?
            </h3>

            <div className="text-left max-w-4xl mx-auto space-y-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Urgences médicales :
                  </span>{" "}
                  Si vous ou un compagnon de voyage tombez gravement malade ou
                  êtes blessé.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Urgences familiales :
                  </span>{" "}
                  Si un proche membre de la famille tombe gravement malade ou
                  décède.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Catastrophes naturelles :
                  </span>{" "}
                  Ouragans, tremblements de terre ou inondations qui vous
                  empêchent de poursuivre votre voyage.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Problèmes avec le prestataire de voyage :
                  </span>{" "}
                  Si votre compagnie aérienne ou votre voyagiste fait faillite.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Achats essentiels :
                  </span>{" "}
                  Articles de toilette, vêtements et autres articles nécessaires
                  pendant que vous attendez vos bagages.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Retard prolongé :
                  </span>{" "}
                  La couverture s&apos;active généralement après un nombre d&apos;heures
                  déterminé, comme 6 ou 12 heures.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] shrink-0 mt-1" />
                <p className="text-lg">
                  <span className="font-semibold text-[#49628b]">
                    Livraison retardée :
                  </span>{" "}
                  Si vos bagages sont retardés au-delà du nombre d&apos;heures
                  spécifié, l&apos;assurance peut vous rembourser les articles
                  essentiels que vous avez dû acheter.
                </p>
              </div>
            </div>

            <div className="mt-12 flex justify-center">
              <Link
                to="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Protégez votre voyage
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ASSURANCE MÉDICALE D'URGENCE */}
      <section
        className="relative py-20 sm:py-24 md:py-22 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 ${container}`}>
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Assurance médicale d&apos;urgence : votre bouée de sauvetage à l&apos;étranger
            </h2>

            <p className="text-lg sm:text-xl font-semibold mb-4">
              Bienvenue à l&apos;assurance médicale d&apos;urgence au Canada
            </p>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-2 text-left sm:text-center">
              Les urgences peuvent survenir à tout moment, n&apos;importe où.
            </p>
            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 text-left sm:text-center">
              L&apos;assurance médicale d&apos;urgence vous apporte la tranquillité
              d&apos;esprit. Elle garantit que vous et vos proches êtes protégés
              dans des situations inattendues.
            </p>

            <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">
              Qu&apos;est-ce que l&apos;assurance médicale d&apos;urgence ?
            </h3>
            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-10 sm:mb-12 text-left sm:text-center">
              L&apos;assurance médicale d&apos;urgence est conçue pour couvrir les
              dépenses médicales imprévues en cas d&apos;accident ou de maladie
              soudaine. Elle aide à payer les hospitalisations, les chirurgies,
              les médicaments sur ordonnance et d&apos;autres services médicaux
              essentiels.
            </p>

            <p className="text-lg sm:text-xl font-bold mb-6 sm:mb-8">
              Convient pour :
            </p>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              <div className="bg-white/95 rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-[#49628b] font-semibold text-lg sm:text-xl flex items-center justify-center text-center">
                Canadiens voyageant à l&apos;extérieur du Canada
              </div>

              <div className="bg-white/95 rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-[#49628b] font-semibold text-lg sm:text-xl flex items-center justify-center text-center">
                Étudiants partant étudier à l&apos;étranger
              </div>

              <div className="bg-white/95 rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-[#49628b] font-semibold text-lg sm:text-xl flex items-center justify-center text-center">
                Canadiens voyageant hors de leur province
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUE COUVRE-T-ELLE ? */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800 text-center">
            <h2 className="text-3xl sm:text-3xl font-bold text-[#49628b] mb-4">
              Que couvre-t-elle ?
            </h2>
            <p className="text-base sm:text-lg max-w-3xl mx-auto leading-7 sm:leading-8 mb-12 text-left sm:text-center">
              L&apos;assurance médicale d&apos;urgence couvre généralement les éléments
              suivants :
            </p>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Hospitalisations
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Frais liés à la chambre et à la pension hospitalière.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Services médicaux
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Honoraires des médecins, infirmières et spécialistes.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Interventions chirurgicales
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Frais associés aux chirurgies nécessaires.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Diagnostics
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Analyses de laboratoire, radiographies et autres services
                    diagnostiques.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Services d&apos;ambulance
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Frais de transport d&apos;urgence.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Médicaments sur ordonnance
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Médicaments prescrits lors d&apos;une urgence.
                  </p>
                </div>
              </div>

              <div className="bg-[#28486f] text-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg text-left md:col-span-1 md:col-start-2">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Rapatriement
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Frais de retour chez vous en cas de maladie grave ou de
                    décès.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exclusions à connaître */}
      <section className="py-16 sm:py-20 md:py-22 bg-gradient-to-r from-[#335a7b] via-[#3687a8] to-[#34a9cf]">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Exclusions à connaître :
            </h2>

            <div className="mt-10 grid gap-6 sm:gap-8 md:grid-cols-3 text-left">
              <div className="bg-[#396f93] rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Conditions préexistantes :
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00c4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Les conditions existant avant la souscription de
                    l&apos;assurance peuvent ne pas être couvertes à moins d&apos;avoir
                    souscrit le bon régime.
                  </p>
                </div>
              </div>

              <div className="bg-[#396f93] rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Activités à risque élevé :
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00c4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Les blessures résultant d&apos;activités comme les sports
                    extrêmes peuvent être exclues.
                  </p>
                </div>
              </div>

              <div className="bg-[#396f93] rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">
                  Procédures électives :
                </h3>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#00c4ff] mt-1 shrink-0" />
                  <p className="text-sm sm:text-base leading-relaxed">
                    Les traitements non urgents et les chirurgies esthétiques
                    ne sont généralement pas couverts.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Obtenir une soumission !
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT CHOISIR LE BON RÉGIME */}
      <section className="relative pt-20 sm:pt-24 md:pt-22 pb-16 sm:pb-20 md:pb-24 bg-[#dce2ea]">
        <div className="absolute -top-12 left-0 w-full h-12 bg-white"></div>
        <div className="absolute -top-[4px] left-0 w-full h-[4px] bg-[#2b4672]"></div>
        <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#2b4672]"></div>

        <div className={container}>
          <div className="max-w-6xl mx-auto text-center text-gray-800">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-10">
              Comment choisir le bon régime
            </h2>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              <div className="bg-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg border border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Évaluez vos besoins
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Tenez compte de votre destination, de la durée de votre
                  voyage et des activités auxquelles vous participerez.
                </p>
              </div>

              <div className="bg-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg border border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Comparez les régimes
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Examinez les différentes options et ce qu&apos;elles offrent.
                  Portez attention aux limites de couverture et aux exclusions.
                </p>
              </div>

              <div className="bg-white rounded-3xl px-6 py-8 sm:px-8 sm:py-10 shadow-lg border border-gray-200">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Consultez un expert
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chez einsured.ca, nos conseillers expérimentés peuvent vous
                  aider à choisir le meilleur régime selon vos besoins.
                </p>
              </div>
            </div>

            <div className="mt-12 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Réserver une réunion maintenant !
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers blogues" mode="related" relatedCount={3} />
    </>
  );
}
